/**
 * Case-setup state for the Pipeline tab.
 *
 * Division of labour with the shell store (`@/store/appStore`, owner W-C):
 *   - `appStore` owns the current case, the live `JobStatus` and the log buffer,
 *     and is the only place subscribed to the `job://*` events.
 *   - this store owns the *draft* a user is editing before a case exists —
 *     folders, subject id, pipeline kind, stage selection, the scan result —
 *     plus the inline notice strip on the setup screen.
 *
 * Stage wall-clock timings are tracked in `./timings` because the store's job
 * patcher only carries stage *state*; `startedIso`/`finishedIso` stay null on a
 * live run until the backend reconciles them.
 */

import { create } from "zustand";
import {
  cancelJob,
  createCase,
  refreshCaseNodes,
  saveCase,
  scanDatasetRoot,
  scanSubject,
  splitError,
  startJob,
} from "@/ipc/api";
import type {
  CaseDraft,
  CaseManifest,
  DatasetSubjectRef,
  DwiStage,
  JobState,
  JobStatus,
  Modality,
  ParamValue,
  PipelineKind,
  PwiStage,
  StageId,
  StageStatus,
  SubjectScan,
} from "@/ipc/contract";
import { appActions, useAppStore,
  reconcileJob,
} from "@/store/appStore";
import { autoOverlayPrediction } from "@/viewer/actions";
import { stillFollowing } from "@/viewer/follow";
import { useViewerStore } from "@/viewer/viewerStore";
import { useResultsStore } from "@/results/state";
import { batchIsOver, planLaunch } from "./batch";
import { applyFilter, FILTER_PRESETS, type FilterPreset } from "./filter";
import { baseName, parentDir } from "./paths";
import { fullSelection, modalitiesFor, sortSelection, stagesFor } from "./stages";
import { resetStageTimings } from "./timings";

export interface SetupDraft {
  subjectDir: string;
  /** Combined runs may point the PWI half at a different subject folder. */
  pwiSubjectDir: string;
  outputRoot: string;
  subjectId: string;
  studyName: string;
  pipelineKind: PipelineKind;
  gpuIndex: string;
  /** null = "Automatic": let the backend pick the best available image. */
  dockerImage: string | null;
  stages: { dwi: DwiStage[]; pwi: PwiStage[] };
  cleanupEnabled: boolean;
}

export interface Notice {
  tone: "info" | "error";
  message: string;
  detail: string | null;
}

export type PipelineMode = "setup" | "running";
export type DataMode = "subject" | "dataset";

interface SessionState {
  mode: PipelineMode;
  draft: SetupDraft;
  /** Set once the user edits Subject ID by hand so a re-scan stops overwriting it. */
  subjectIdTouched: boolean;
  scan: SubjectScan | null;
  scanning: boolean;
  scanError: string | null;
  busy: boolean;
  notice: Notice | null;
  /** `${modality}:${stageId}` of the stage whose Properties are shown. */
  selectedStageKey: string | null;

  dataMode: DataMode;
  datasetRoot: string;
  subjectGlob: string;
  fileGlob: string;
  datasetSubjects: DatasetSubjectRef[];
  datasetScanning: boolean;
  datasetError: string | null;
  selectedSubjectIds: string[];
  /** Remaining subject dirs after the one currently running. */
  batchQueue: string[];
  batchTotal: number;
  batchIndex: number;

  setMode: (mode: PipelineMode) => void;
  patchDraft: (patch: Partial<SetupDraft>) => void;
  setSubjectId: (value: string) => void;
  setPipelineKind: (kind: PipelineKind) => void;
  toggleStage: (modality: Modality, id: StageId) => void;
  /** Check a stage without unchecking others. */
  ensureStage: (modality: Modality, id: StageId) => void;
  selectAllStages: () => void;
  clearStages: () => void;
  chooseSubjectDir: (dir: string) => Promise<void>;
  chooseDataDir: (dir: string) => Promise<void>;
  chooseDatasetRoot: (dir: string) => Promise<void>;
  setDataMode: (mode: DataMode) => void;
  setFilter: (patch: { subjectGlob?: string; fileGlob?: string; preset?: FilterPreset }) => void;
  focusSubject: (dir: string) => Promise<void>;
  toggleMatchedSubject: (subjectId: string) => void;
  selectAllMatched: (ids: string[]) => void;
  /**
   * One run has ended. `jobId` says which, because a batch can have several in
   * flight and only the one in focus should move the viewer.
   */
  onJobSettled: (state: JobState, jobId?: string) => void;
  rescan: () => Promise<void>;
  saveSetup: () => Promise<void>;
  createCaseFromDraft: () => Promise<CaseManifest | null>;
  run: (only?: { dwi: DwiStage[]; pwi: PwiStage[] }) => Promise<void>;
  runOne: (dir: string, stages: { dwi: DwiStage[]; pwi: PwiStage[] }) => Promise<void>;
  restartFailed: () => Promise<void>;
  stop: () => Promise<void>;
  selectStage: (key: string | null) => void;
  setNotice: (notice: Notice | null) => void;

  /**
   * Pipeline parameters the reader changed, by dotted config path. Kept on the
   * session rather than the draft: a changed b-value belongs to the run being
   * set up, not to the case on disk, and carrying it in the manifest would make
   * a case reopened next week claim a parameter the app can no longer honour.
   */
  paramOverrides: Record<string, ParamValue>;
  /** `null` clears the override, putting the parameter back to the default. */
  setParamOverride: (key: string, value: ParamValue | null) => void;
  resetParamOverrides: () => void;
  /** New Analysis card on Home: preload a pipeline kind and its stage list. */
  startNewAnalysis: (kind: PipelineKind, preset: "full" | "blank") => void;
  adoptCase: (manifest: CaseManifest) => void;
  /** Drops the open case, the draft and the live job. */
  closeCase: () => void;
}

function emptyDraft(): SetupDraft {
  return {
    subjectDir: "",
    pwiSubjectDir: "",
    outputRoot: "",
    subjectId: "",
    studyName: "",
    pipelineKind: "dwi",
    gpuIndex: "0",
    dockerImage: null,
    stages: fullSelection("dwi"),
    cleanupEnabled: true,
  };
}

/**
 * Re-read the outputs a completed run produced, then show them.
 *
 * Best-effort throughout: a refresh that fails must not turn a successful run
 * into an error, because the run really did succeed and the files really are
 * on disk. The Study Explorer's own Refresh is the fallback.
 */
async function adoptRunOutputs(): Promise<void> {
  const manifest = useAppStore.getState().currentCase;
  if (!manifest) return;
  try {
    const updated = await refreshCaseNodes(manifest.caseId);
    // The user may have closed or switched case while the run finished.
    if (useAppStore.getState().currentCase?.caseId !== manifest.caseId) return;
    appActions.setCurrentCase(updated);
    // The run followed each stage onto the screen as it finished. If the reader
    // took the viewport over while it was going, they are looking at something
    // deliberately, and the final prediction must not shove it aside.
    if (stillFollowing(useViewerStore.getState().baseNodeId)) {
      autoOverlayPrediction(updated);
    }
    void useResultsStore.getState().loadSummary(true);
  } catch {
    // Leave the tree as it was; nothing here is worth interrupting the user.
  }
}

/**
 * How many subjects may be in flight at once. Decided by the Rust side from the
 * machine's memory and the reader's preference (`concurrency::budget`); 1 until
 * the first system status has arrived, which is the safe way to be wrong.
 */
function concurrencyLimit(): number {
  return Math.max(1, useAppStore.getState().systemStatus?.concurrency.limit ?? 1);
}

/** Runs of this batch that have not settled yet. */
function inFlightCount(): number {
  return Object.values(useAppStore.getState().jobs).filter(
    (j) => j.state === "running" || j.state === "queued",
  ).length;
}

const DEFAULT_FILTER = FILTER_PRESETS[0];

function emptyDataset() {
  return {
    dataMode: "subject" as DataMode,
    datasetRoot: "",
    subjectGlob: DEFAULT_FILTER.subjectGlob,
    fileGlob: DEFAULT_FILTER.fileGlob,
    datasetSubjects: [] as DatasetSubjectRef[],
    datasetScanning: false,
    datasetError: null as string | null,
    selectedSubjectIds: [] as string[],
    batchQueue: [] as string[],
    batchTotal: 0,
    batchIndex: 0,
  };
}

function matchedIds(subjects: DatasetSubjectRef[], subjectGlob: string, fileGlob: string): string[] {
  return applyFilter(subjects, subjectGlob, fileGlob).map((s) => s.subjectId);
}

function syncSelected(subjects: DatasetSubjectRef[], subjectGlob: string, fileGlob: string, prev: string[]): string[] {
  const matched = matchedIds(subjects, subjectGlob, fileGlob);
  const kept = prev.filter((id) => matched.includes(id));
  return kept.length > 0 ? kept : matched;
}

/** Fills output / GPU / image from saved preferences when the draft is blank. */
function withPrefs(draft: SetupDraft): SetupDraft {
  const prefs = useAppStore.getState().preferences;
  if (!prefs) return draft;
  return {
    ...draft,
    outputRoot: draft.outputRoot || prefs.defaultOutputRoot || "",
    gpuIndex: draft.gpuIndex || prefs.defaultGpuIndex || "0",
    dockerImage: draft.dockerImage ?? prefs.defaultDockerImage,
    cleanupEnabled: prefs.cleanupEnabled,
  };
}

/** Stage rows in execution order: the DWI half first, then the PWI half. */
export function buildStageStatuses(
  kind: PipelineKind,
  stages: { dwi: DwiStage[]; pwi: PwiStage[] },
): StageStatus[] {
  const rows: StageStatus[] = [];
  for (const modality of modalitiesFor(kind)) {
    const ids: StageId[] = modality === "DWI" ? stages.dwi : stages.pwi;
    for (const meta of stagesFor(modality)) {
      if (!ids.includes(meta.id)) continue;
      rows.push({
        id: meta.id,
        modality,
        title: meta.title,
        state: "pending",
        startedIso: null,
        finishedIso: null,
        durationSec: null,
      });
    }
  }
  return rows;
}

function toCaseDraft(draft: SetupDraft): CaseDraft {
  return {
    subjectDir: draft.subjectDir,
    outputRoot: draft.outputRoot,
    subjectId: draft.subjectId,
    studyName: draft.studyName,
    pipelineKind: draft.pipelineKind,
    pwiSubjectDir: draft.pipelineKind === "combined" ? draft.pwiSubjectDir || null : null,
    gpuIndex: draft.gpuIndex,
    dockerImage: draft.dockerImage,
    stages: draft.stages,
    cleanupEnabled: draft.cleanupEnabled,
  };
}

export const usePipelineSession = create<SessionState>((set, get) => ({
  mode: "setup",
  draft: emptyDraft(),
  subjectIdTouched: false,
  scan: null,
  scanning: false,
  scanError: null,
  busy: false,
  notice: null,
  paramOverrides: {},
  setParamOverride: (key, value) =>
    set((state) => {
      const next = { ...state.paramOverrides };
      if (value === null) delete next[key];
      else next[key] = value;
      return { paramOverrides: next };
    }),
  resetParamOverrides: () => set({ paramOverrides: {} }),
  selectedStageKey: null,
  ...emptyDataset(),

  setMode: (mode) => set({ mode }),

  patchDraft: (patch) => set((s) => ({ draft: { ...s.draft, ...patch } })),

  setSubjectId: (value) =>
    set((s) => ({ draft: { ...s.draft, subjectId: value }, subjectIdTouched: true })),

  setPipelineKind: (kind) =>
    set((s) => ({
      draft: {
        ...s.draft,
        pipelineKind: kind,
        // A DWI-only run must not ship PWI stages, and vice versa; whichever
        // half becomes active starts from the full list.
        stages: {
          dwi: kind === "pwi" ? [] : s.draft.stages.dwi.length ? s.draft.stages.dwi : fullSelection(kind).dwi,
          pwi: kind === "dwi" ? [] : s.draft.stages.pwi.length ? s.draft.stages.pwi : fullSelection(kind).pwi,
        },
      },
    })),

  toggleStage: (modality, id) =>
    set((s) => {
      const key = modality === "DWI" ? "dwi" : "pwi";
      const current: StageId[] = s.draft.stages[key];
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      return {
        draft: {
          ...s.draft,
          stages: { ...s.draft.stages, [key]: sortSelection(modality, next) } as SetupDraft["stages"],
        },
      };
    }),

  ensureStage: (modality, id) =>
    set((s) => {
      const key = modality === "DWI" ? "dwi" : "pwi";
      if ((s.draft.stages[key] as StageId[]).includes(id)) return s;
      return {
        draft: {
          ...s.draft,
          stages: {
            ...s.draft.stages,
            [key]: sortSelection(modality, [...s.draft.stages[key], id]),
          } as SetupDraft["stages"],
        },
      };
    }),

  selectAllStages: () =>
    set((s) => ({ draft: { ...s.draft, stages: fullSelection(s.draft.pipelineKind) } })),

  clearStages: () => set((s) => ({ draft: { ...s.draft, stages: { dwi: [], pwi: [] } } })),

  chooseSubjectDir: async (dir) => {
    const prefs = useAppStore.getState().preferences;
    set((s) => ({
      dataMode: "subject",
      draft: {
        ...s.draft,
        subjectDir: dir,
        // Subject ID defaults to the folder name; still editable afterwards.
        subjectId: s.subjectIdTouched ? s.draft.subjectId : baseName(dir),
        // A sibling openads-out folder is enough to Create Case immediately;
        // the user can still point it at another disk from the setup form.
        outputRoot:
          s.draft.outputRoot ||
          prefs?.defaultOutputRoot ||
          `${parentDir(dir)}/openads-out`,
      },
    }));
    await get().rescan();
  },

  chooseDataDir: async (dir) => {
    set({ scanning: true, scanError: null });
    try {
      const scan = await scanSubject(dir);
      if (scan.isDatasetRoot) {
        await get().chooseDatasetRoot(dir);
        return;
      }
      await get().chooseSubjectDir(dir);
    } catch (error) {
      const { message } = splitError(error);
      set({ scanning: false, scan: null, scanError: message });
    }
  },

  chooseDatasetRoot: async (dir) => {
    const prefs = useAppStore.getState().preferences;
    set((s) => ({
      dataMode: "dataset",
      datasetRoot: dir,
      datasetScanning: true,
      datasetError: null,
      scan: null,
      scanError: null,
      scanning: false,
      mode: "setup",
      draft: {
        ...s.draft,
        outputRoot: s.draft.outputRoot || prefs?.defaultOutputRoot || `${dir}/openads-out`,
      },
    }));
    try {
      const { subjects } = await scanDatasetRoot(dir);
      const { subjectGlob, fileGlob } = get();
      const selectedSubjectIds = matchedIds(subjects, subjectGlob, fileGlob);
      set({
        datasetSubjects: subjects,
        datasetScanning: false,
        selectedSubjectIds,
        notice:
          subjects.length === 0
            ? { tone: "error", message: "No subject folders with NIfTI were found in that directory.", detail: dir }
            : {
                tone: "info",
                message: `${selectedSubjectIds.length} of ${subjects.length} folders match the filter.`,
                detail: null,
              },
      });
      const first = subjects.find((s) => selectedSubjectIds.includes(s.subjectId));
      if (first) await get().focusSubject(first.dir);
    } catch (error) {
      const { message } = splitError(error);
      set({ datasetScanning: false, datasetSubjects: [], datasetError: message });
    }
  },

  setDataMode: (mode) => {
    set({ dataMode: mode, batchQueue: [], batchTotal: 0, batchIndex: 0 });
  },

  setFilter: (patch) =>
    set((s) => {
      const subjectGlob = patch.preset?.subjectGlob ?? patch.subjectGlob ?? s.subjectGlob;
      const fileGlob = patch.preset?.fileGlob ?? patch.fileGlob ?? s.fileGlob;
      return {
        subjectGlob,
        fileGlob,
        selectedSubjectIds: syncSelected(s.datasetSubjects, subjectGlob, fileGlob, s.selectedSubjectIds),
      };
    }),

  focusSubject: async (dir) => {
    set((s) => ({
      draft: {
        ...s.draft,
        subjectDir: dir,
        subjectId: s.subjectIdTouched ? s.draft.subjectId : baseName(dir),
      },
      subjectIdTouched: false,
    }));
    await get().rescan();
  },

  toggleMatchedSubject: (subjectId) =>
    set((s) => {
      const has = s.selectedSubjectIds.includes(subjectId);
      const next = has
        ? s.selectedSubjectIds.filter((id) => id !== subjectId)
        : [...s.selectedSubjectIds, subjectId];
      return { selectedSubjectIds: next.length ? next : [subjectId] };
    }),

  selectAllMatched: (ids) => set({ selectedSubjectIds: ids }),

  rescan: async () => {
    const { draft, dataMode, datasetRoot } = get();
    if (dataMode === "dataset" && datasetRoot) {
      set({ datasetScanning: true, datasetError: null });
      try {
        const { subjects } = await scanDatasetRoot(datasetRoot);
        const { subjectGlob, fileGlob, selectedSubjectIds } = get();
        set({
          datasetSubjects: subjects,
          datasetScanning: false,
          selectedSubjectIds: syncSelected(subjects, subjectGlob, fileGlob, selectedSubjectIds),
        });
      } catch (error) {
        const { message } = splitError(error);
        set({ datasetScanning: false, datasetError: message });
      }
    }
    if (!draft.subjectDir) {
      if (dataMode !== "dataset") {
        set({ notice: { tone: "error", message: "Choose an input folder first.", detail: null } });
      }
      return;
    }
    set({ scanning: true, scanError: null });
    try {
      const scan = await scanSubject(draft.subjectDir);
      appActions.setCurrentScan(scan);
      set((s) => ({
        scan,
        scanning: false,
        draft: {
          ...s.draft,
          subjectId: s.subjectIdTouched ? s.draft.subjectId : scan.subjectId || s.draft.subjectId,
        },
      }));
    } catch (error) {
      const { message } = splitError(error);
      set({ scanning: false, scan: null, scanError: message });
    }
  },

  saveSetup: async () => {
    const manifest = useAppStore.getState().currentCase;
    if (!manifest) {
      set({
        notice: {
          tone: "info",
          message: "Nothing to save yet — Create Case writes case.json for the first time.",
          detail: null,
        },
      });
      return;
    }
    set({ busy: true });
    try {
      const { draft } = get();
      // Only the fields the setup screen actually edits are written back; the
      // manifest's roots and rel-paths stay as the backend computed them.
      const updated: CaseManifest = {
        ...manifest,
        subjectId: draft.subjectId,
        studyName: draft.studyName,
        pipelineKind: draft.pipelineKind,
        outputRoot: draft.outputRoot,
        gpuIndex: draft.gpuIndex,
        dockerImage: draft.dockerImage,
        stages: draft.stages,
        cleanupEnabled: draft.cleanupEnabled,
        modifiedIso: new Date().toISOString(),
      };
      await saveCase(updated);
      appActions.setCurrentCase(updated);
      set({ busy: false, notice: { tone: "info", message: "Case setup saved.", detail: null } });
    } catch (error) {
      const { message, detail } = splitError(error);
      set({ busy: false, notice: { tone: "error", message, detail } });
    }
  },

  createCaseFromDraft: async () => {
    const { draft } = get();
    if (!draft.subjectDir) {
      set({ notice: { tone: "error", message: "Choose a subject folder first.", detail: null } });
      return null;
    }
    if (!draft.outputRoot) {
      set({
        notice: { tone: "error", message: "Choose an output folder before creating the case.", detail: null },
      });
      return null;
    }
    if (!draft.subjectId.trim()) {
      set({ notice: { tone: "error", message: "Subject ID cannot be empty.", detail: null } });
      return null;
    }
    set({ busy: true, notice: null });
    try {
      const manifest = await createCase(toCaseDraft(draft));
      appActions.setCurrentCase(manifest);
      set({
        busy: false,
        notice: { tone: "info", message: `Case ${manifest.subjectId} created.`, detail: null },
      });
      return manifest;
    } catch (error) {
      const { message, detail } = splitError(error);
      set({ busy: false, notice: { tone: "error", message, detail } });
      return null;
    }
  },

  run: async (only) => {
    const { draft, dataMode, datasetSubjects, selectedSubjectIds, subjectGlob, fileGlob } = get();
    const stages = only ?? draft.stages;
    if (!stages.dwi.length && !stages.pwi.length) {
      set({ notice: { tone: "error", message: "Select at least one stage before running.", detail: null } });
      return;
    }
    if (dataMode === "dataset") {
      const matched = applyFilter(datasetSubjects, subjectGlob, fileGlob);
      const dirs = matched.filter((s) => selectedSubjectIds.includes(s.subjectId)).map((s) => s.dir);
      if (dirs.length === 0) {
        set({
          notice: { tone: "error", message: "No matching subjects are checked.", detail: null },
        });
        return;
      }
      if (dirs.length === 1) {
        set({ batchQueue: [], batchTotal: 0, batchIndex: 0 });
        await get().runOne(dirs[0], stages);
        return;
      }
      // Start as many as the machine will hold and queue the rest. The engine
      // admits `limit` of them and the others sit in `queued` until a slot
      // frees, so the ceiling is enforced where the memory is, not here.
      const { start: head, queue } = planLaunch(dirs, 0, concurrencyLimit());
      set({
        batchQueue: queue,
        batchTotal: dirs.length,
        batchIndex: head.length,
      });
      // Serially, even though they run in parallel: `runOne` walks the draft
      // onto each subject in turn, so two overlapping calls would race on it.
      for (const dir of head) {
        await get().runOne(dir, stages);
      }
      return;
    }
    await get().runOne(draft.subjectDir, stages);
  },

  runOne: async (dir, stages) => {
    if (!dir) {
      set({ notice: { tone: "error", message: "Choose a subject folder first.", detail: null } });
      return;
    }
    if (get().draft.subjectDir !== dir) {
      await get().focusSubject(dir);
    }
    const { draft, batchIndex, batchTotal } = get();
    const manifest = await get().createCaseFromDraft();
    if (!manifest) return;

    set({ busy: true });
    try {
      const handle = await startJob({
        caseId: manifest.caseId,
        pipelineKind: draft.pipelineKind,
        stages,
        gpuIndex: draft.gpuIndex,
        dockerImage: draft.dockerImage,
        cleanupEnabled: draft.cleanupEnabled,
        configOverrides: get().paramOverrides,
      });
      const rows = buildStageStatuses(draft.pipelineKind, stages);
      const job: JobStatus = {
        jobId: handle.jobId,
        caseId: manifest.caseId,
        subjectId: manifest.subjectId,
        state: "running",
        engine: handle.engine,
        deviceLabel: handle.deviceLabel,
        deviceClass: handle.deviceClass,
        containerId: null,
        startedIso: new Date().toISOString(),
        finishedIso: null,
        exitCode: null,
        progressPct: 0,
        currentStage: rows[0]?.id ?? null,
        currentModality: rows[0]?.modality ?? null,
        stages: rows,
        errorMessage: null,
        health: null,
      };
      resetStageTimings();
      appActions.clearLog();
      appActions.setJob(job);
      set({
        busy: false,
        mode: "running",
        selectedStageKey: rows[0] ? `${rows[0].modality}:${rows[0].id}` : null,
        notice:
          batchTotal > 1
            ? {
                tone: "info",
                message: `Running ${batchIndex} of ${batchTotal} — ${manifest.subjectId}`,
                detail: null,
              }
            : null,
      });
    } catch (error) {
      const { message, detail } = splitError(error);
      set({ busy: false, notice: { tone: "error", message, detail } });
    }
  },

  onJobSettled: (state, jobId) => {
    const { batchQueue, batchTotal, batchIndex, draft } = get();
    const isFocus = !jobId || useAppStore.getState().job?.jobId === jobId;
    // A finished run has written files the case tree has never seen. Without
    // this the mask exists on disk, the viewer shows the inputs it showed
    // before, and Results still holds whatever it read last - which looks
    // exactly like a run that did nothing.
    // Only for the run the workspace is following: adopting a sibling's outputs
    // would refresh the open case because a different subject finished, and move
    // the viewer with it.
    if (state === "completed" && isFocus) void adoptRunOutputs();
    if (state === "cancelled") {
      set({
        batchQueue: [],
        batchTotal: 0,
        batchIndex: 0,
        notice: { tone: "info", message: "Batch stopped.", detail: null },
      });
      return;
    }
    if (batchQueue.length === 0) {
      // With several in flight, the batch is only over when the last of them
      // settles — the queue emptying just means nothing more is waiting.
      if (batchTotal > 1 && batchIsOver(batchQueue, inFlightCount())) {
        set({
          batchIndex: 0,
          batchTotal: 0,
          notice: {
            tone: state === "completed" ? "info" : "error",
            message:
              state === "completed"
                ? `Finished ${batchTotal} matching subjects.`
                : `Batch ended after ${batchIndex} of ${batchTotal} (${state}).`,
            detail: null,
          },
        });
      }
      return;
    }
    // Top back up to the limit rather than starting exactly one: a run that
    // failed in its first second should not leave a slot idle.
    const { start: next, queue } = planLaunch(batchQueue, inFlightCount(), concurrencyLimit());
    if (next.length === 0) return;
    set({ batchQueue: queue, batchIndex: batchIndex + next.length });
    void (async () => {
      for (const dir of next) {
        await get().runOne(dir, draft.stages);
      }
    })();
  },

  restartFailed: async () => {
    const job = useAppStore.getState().job;
    if (!job) {
      set({ notice: { tone: "info", message: "No previous run to restart.", detail: null } });
      return;
    }
    const unfinished = job.stages.filter((s) => s.state !== "completed");
    if (!unfinished.length) {
      set({ notice: { tone: "info", message: "Nothing to restart — every stage completed.", detail: null } });
      return;
    }
    await get().run({
      dwi: sortSelection("DWI", unfinished.filter((s) => s.modality === "DWI").map((s) => s.id as DwiStage)),
      pwi: sortSelection("PWI", unfinished.filter((s) => s.modality === "PWI").map((s) => s.id as PwiStage)),
    });
  },

  stop: async () => {
    // Every run in flight, not just the one on screen: with two subjects going,
    // stopping the one in focus and leaving its sibling to finish is not what
    // anybody means by Stop.
    const live = Object.values(useAppStore.getState().jobs).filter(
      (j) => j.state === "running" || j.state === "queued",
    );
    if (live.length === 0) return;
    // Clear the queue first: stopping a batch has to stop the batch, not hand
    // the next subject straight to the engine.
    set({ batchQueue: [], batchTotal: 0, batchIndex: 0 });

    const failures: string[] = [];
    for (const job of live) {
      try {
        await cancelJob(job.jobId);
      } catch (error) {
        failures.push(splitError(error).message);
      }
    }
    if (failures.length === 0) return;
    // The usual cause is a job the engine no longer has — it restarted, and this
    // window kept showing a run that had already gone. Stop must still leave the
    // interface telling the truth, so reconcile rather than reporting a failure
    // and carrying on claiming something is running.
    await reconcileJob();
    const stillLive = Object.values(useAppStore.getState().jobs).some(
      (j) => j.state === "running" || j.state === "queued",
    );
    if (stillLive) {
      set({ notice: { tone: "error", message: failures[0], detail: failures.slice(1).join("\n") || null } });
    }
  },

  selectStage: (key) => set({ selectedStageKey: key }),

  setNotice: (notice) => set({ notice }),

  startNewAnalysis: (kind, preset) => {
    appActions.setJob(null);
    appActions.setCurrentCase(null);
    appActions.setCurrentScan(null);
    appActions.clearLog();
    resetStageTimings();
    set({
      mode: "setup",
      notice: null,
      scan: null,
      scanError: null,
      selectedStageKey: null,
      subjectIdTouched: false,
      ...emptyDataset(),
      draft: withPrefs({
        ...emptyDraft(),
        pipelineKind: kind,
        stages: preset === "full" ? fullSelection(kind) : { dwi: [], pwi: [] },
      }),
    });
  },

  closeCase: () => {
    appActions.setJob(null);
    appActions.setCurrentCase(null);
    appActions.setCurrentScan(null);
    appActions.setSelectedNodeId(null);
    appActions.clearLog();
    appActions.setResultsAvailable(null);
    resetStageTimings();
    set({
      mode: "setup",
      notice: null,
      scan: null,
      scanError: null,
      selectedStageKey: null,
      subjectIdTouched: false,
      ...emptyDataset(),
      draft: withPrefs(emptyDraft()),
    });
  },

  adoptCase: (manifest) => {
    appActions.setCurrentCase(manifest);
    set({
      mode: "setup",
      subjectIdTouched: true,
      notice: null,
      draft: {
        // E2E test patch: an empty subjectRelPath left a trailing "/" that no
        // longer matched the browser fileStore key.
        subjectDir: manifest.subjectRelPath
          ? `${manifest.inputRoot}/${manifest.subjectRelPath}`
          : manifest.inputRoot,
        pwiSubjectDir: manifest.pwiSubjectRelPath
          ? `${manifest.inputRoot}/${manifest.pwiSubjectRelPath}`
          : "",
        outputRoot: manifest.outputRoot,
        subjectId: manifest.subjectId,
        studyName: manifest.studyName,
        pipelineKind: manifest.pipelineKind,
        gpuIndex: manifest.gpuIndex,
        dockerImage: manifest.dockerImage,
        stages: manifest.stages,
        cleanupEnabled: manifest.cleanupEnabled,
      },
    });
    void get().rescan();
  },
}));
